import { useEffect } from 'react';

/*
 * Ambient colour (ui-ux.md §2 "Ambient wash"): a view led by one title tints
 * the canvas with that title's palette. `useAmbient(palette)` writes the
 * chosen colour as space-separated RGB to `--ambient` on the app shell for
 * the view's lifetime; `<AmbientStage>` (AppShell) paints the wash from it.
 * Unset → no wash, the neutral canvas.
 */

type Rgb = [number, number, number];

/** OKLCH bounds the tint is clamped into, so a near-black or neon palette still reads as a tint. */
const L_MIN = 0.6;
const L_MAX = 0.66;
const C_MAX = 0.14;
/** The lightness a pick is scored against ("mid lightness"). */
const L_MID = 0.62;
/** Below this chroma a palette is grey: a grey wash is haze, not colour → no wash. */
const C_MIN = 0.02;

function parseHex(value: string): Rgb | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = [...h].map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
}

const toLinear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (v: number) =>
  255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

/** sRGB (0–255) → OKLab (Björn Ottosson's matrices). */
function toOklab([r8, g8, b8]: Rgb): [number, number, number] {
  const [r, g, b] = [toLinear(r8), toLinear(g8), toLinear(b8)];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab → linear sRGB (may fall outside 0–1 when out of gamut). */
function oklabToLinear(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = (rgb: number[]) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/**
 * The palette entry to tint with, as `"R G B"` (0–255): the most saturated
 * entry nearest mid lightness, clamped to OKLCH L 0.60–0.66 and C ≤ 0.14 and
 * gamut-mapped by reducing chroma. `null` for an empty/unparseable or
 * achromatic palette. The L/C bounds are what keeps every ink ≥ 4.5:1 over
 * the wash at its peak alpha (ui-ux.md §2 "Ambient wash").
 */
export function ambientColor(palette: readonly string[] | null | undefined): string | null {
  let best: { L: number; C: number; h: number; score: number } | null = null;
  for (const entry of palette ?? []) {
    const rgb = parseHex(entry);
    if (!rgb) continue;
    const [L, a, b] = toOklab(rgb);
    const C = Math.hypot(a, b);
    const score = C - 0.5 * Math.abs(L - L_MID);
    if (!best || score > best.score) best = { L, C, h: Math.atan2(b, a), score };
  }
  if (!best || best.C < C_MIN) return null;

  const L = Math.min(L_MAX, Math.max(L_MIN, best.L));
  let C = Math.min(C_MAX, best.C);
  const at = (c: number) => oklabToLinear(L, c * Math.cos(best.h), c * Math.sin(best.h));
  if (!inGamut(at(C))) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(at(mid))) lo = mid;
      else hi = mid;
    }
    C = lo;
  }
  return at(C)
    .map((v) => Math.round(Math.min(255, Math.max(0, fromLinear(Math.min(1, Math.max(0, v)))))))
    .join(' ');
}

// One wash for the whole shell. Views register a colour; the latest mounted
// wins, so a route's exit animation (the leaving page is still mounted while
// the next one mounts) never clears the entering view's colour.
const entries: { rgb: string }[] = [];
let target: HTMLElement | null = null;

function apply() {
  if (!target) return;
  const top = entries[entries.length - 1];
  if (top) target.style.setProperty('--ambient', top.rgb);
  else target.style.removeProperty('--ambient');
}

/** Callback ref for the element that carries `--ambient` (AppShell's root). */
export function ambientTarget(el: HTMLElement | null) {
  if (target && target !== el) target.style.removeProperty('--ambient');
  target = el;
  apply();
}

/**
 * Tint the canvas from `palette` while the calling view is mounted. Pass
 * `null` for "this view is not title-led right now" (e.g. Discover while a
 * search is active) — the wash fades back to the neutral canvas.
 */
export function useAmbient(palette: readonly string[] | null | undefined) {
  const rgb = ambientColor(palette);
  useEffect(() => {
    if (!rgb) return;
    const entry = { rgb };
    entries.push(entry);
    apply();
    return () => {
      entries.splice(entries.indexOf(entry), 1);
      apply();
    };
  }, [rgb]);
}
